import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminDocumentsFacade,
  AdminDocumentUpdateDto,
  BulkUploadResult,
} from './admin-documents.facade';
import {
  AdminDocumentListItem,
  AdminDocumentUploadDto,
  AdminDocumentsFilter,
} from '@models/resources/admin-document.model';
import { DocumentResource } from '@models/resources/document.model';
import { DEMO_DOCUMENTS } from '@data/fixtures/documents.fixtures';
import { dummyError, dummyResponse } from '@data/dummy-response.util';
import { resolveCustomerStub } from '@data/dummy-utils';

/**
 * In-Memory-Variante (Sprint 3, S3-6 Teil 3). Hält eine mutable Kopie
 * der Fixtures, damit Upload/Toggle/Delete für Folge-`list()`-Calls
 * sichtbar bleiben (Demo-UX: frisch hochgeladenes Dokument erscheint
 * ohne Reload in der Liste).
 *
 * Der Customer-`DummyDocumentsFacade` hat eine **eigene** immutable
 * View auf `DEMO_DOCUMENTS` – Admin-Mutationen laufen an ihm vorbei,
 * damit die Customer-Demo zwischen Sprint-3-Tag-4-Runs stabil bleibt.
 * Nach BE-Anschluss (Sprint 4/5) teilen sich beide Facades denselben
 * HTTP-Endpunkt + Backend-Store.
 */
@Injectable()
export class DummyAdminDocumentsFacade implements AdminDocumentsFacade {
  private readonly store: DocumentResource[] = DEMO_DOCUMENTS.map((d) => ({
    ...d,
  }));
  private nextId = Math.max(...DEMO_DOCUMENTS.map((d) => d.id)) + 1;

  list(filter?: AdminDocumentsFilter): Observable<AdminDocumentListItem[]> {
    const classification = filter?.classification ?? 'all';
    const customerId = filter?.customer_id;
    const search = filter?.search?.trim().toLowerCase();
    const dateFrom = filter?.date_from;
    const dateTo = filter?.date_to;

    const filtered = this.store.filter((d) => {
      if (classification !== 'all' && d.classification !== classification) return false;
      if (customerId !== undefined && d.customer_id !== customerId) return false;
      if (dateFrom && d.issue_date < dateFrom) return false;
      if (dateTo && d.issue_date > dateTo) return false;
      if (search && !d.name.toLowerCase().includes(search)) return false;
      return true;
    });

    const enriched = filtered
      .map((d) => this.withCustomer(d))
      .sort((a, b) => b.issue_date.localeCompare(a.issue_date));
    return dummyResponse('admin.documents', enriched);
  }

  bulkUpload(dtos: readonly AdminDocumentUploadDto[]): Observable<BulkUploadResult> {
    const succeeded: AdminDocumentListItem[] = [];
    const failed: { filename: string; reason: string }[] = [];
    for (const dto of dtos) {
      try {
        const created: DocumentResource = {
          id: this.nextId++,
          name: dto.name,
          classification: dto.classification,
          issue_date: dto.issue_date,
          size_bytes: dto.size_bytes,
          visible_to_customer: dto.visible_to_customer,
          customer_id: dto.customer_id,
        };
        this.store.unshift(created);
        succeeded.push(this.withCustomer(created));
      } catch (e) {
        failed.push({ filename: dto.name, reason: (e as Error).message });
      }
    }
    return dummyResponse('admin.documents', { succeeded, failed });
  }

  upload(dto: AdminDocumentUploadDto): Observable<AdminDocumentListItem> {
    const created: DocumentResource = {
      id: this.nextId++,
      name: dto.name,
      classification: dto.classification,
      issue_date: dto.issue_date,
      size_bytes: dto.size_bytes,
      visible_to_customer: dto.visible_to_customer,
      customer_id: dto.customer_id,
    };
    this.store.unshift(created);
    return dummyResponse('admin.documents', this.withCustomer(created));
  }

  update(id: number, dto: AdminDocumentUpdateDto): Observable<AdminDocumentListItem> {
    const index = this.store.findIndex((d) => d.id === id);
    if (index === -1) {
      return dummyError('admin.documents');
    }
    const current = this.store[index];
    const updated: DocumentResource = {
      ...current,
      name: dto.title ?? current.name,
      classification: dto.classification ?? current.classification,
      visible_to_customer: dto.visible_to_customer ?? current.visible_to_customer,
    };
    this.store[index] = updated;
    return dummyResponse('admin.documents', this.withCustomer(updated));
  }

  toggleVisibility(id: number): Observable<AdminDocumentListItem> {
    const current = this.store.find((d) => d.id === id);
    if (!current) {
      return dummyError('admin.documents');
    }
    return this.update(id, { visible_to_customer: !current.visible_to_customer });
  }

  delete(id: number): Observable<void> {
    const index = this.store.findIndex((d) => d.id === id);
    if (index === -1) {
      return dummyError('admin.documents');
    }
    this.store.splice(index, 1);
    return dummyResponse('admin.documents', undefined as unknown as void);
  }

  download(id: number): Observable<Blob> {
    const exists = this.store.some((d) => d.id === id);
    if (!exists) {
      return dummyError('admin.documents');
    }
    // Dummy-Mode hat keine echten Dateien. Leerer Pseudo-Blob mit
    // PDF-MIME genügt, damit der UI-Trigger (Blob → ObjectURL) durchläuft.
    return dummyResponse('admin.documents', new Blob([], { type: 'application/pdf' }));
  }

  downloadBatch(ids: readonly number[]): Observable<Blob> {
    if (ids.length === 0) {
      return dummyError('admin.documents');
    }
    // Im Dummy-Modus gibt es keine echten Dateien. Wir liefern einen
    // leeren ZIP-Pseudo-Blob zurück (MIME gesetzt), damit die UI einen
    // Download-Trigger auslösen kann.
    const blob = new Blob([], { type: 'application/zip' });
    return dummyResponse('admin.documents', blob);
  }

  private withCustomer(d: DocumentResource): AdminDocumentListItem {
    return { ...d, customer: resolveCustomerStub(d.customer_id) };
  }
}
